<template>
  <div class="page-domains">
    <PageHeader>
      <div class="heading">
        <h1>
          <strong class="name">{{ company.name }}</strong>
          / Domain Names
        </h1>
      </div>

      <div class="header-actions">
        <router-link
          class="btn is-tertiary"
          :to="{name: 'domain-search', params: {companyId: companyId}}">
          Find a domain name
        </router-link>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
          <div v-if="hasDomains">
            <table-data
              :rows="domains"
              :headings="[
                {key: 'domain', heading: 'Domain Name'}
              ]"
            >
              <div
                v-for="(domain, index) in domains"
                :slot="'td-' + index + '-domain'"
                :key="index"
              >
                <router-link
                  :to="{
                    name: 'company-domain',
                    params: {
                      companyId: companyId,
                      domain: domain.name
                    }
                  }">

                  {{ domain.name }}
                </router-link>
              </div>
            </table-data>

            <pagination @changePage="changePage" :pagination="pageData">
            </pagination>
          </div>

          <div class="domains-intro" v-else>
            <Sprite name="domain" />

            <p>
              You're not yet hosting any domains with us.
            </p>

            <Btn class="btn-refresh" subtle @click="getData">Refresh</Btn>

            <router-link
              class="btn is-primary"
              :to="{name: 'domain-search', params: {companyId: companyId}}"
            >
              Start Search
            </router-link>
          </div>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>My Domain Names</h1>
            <p>
              You can assign a domain name to one of your websites or set up URL forwarding.
            </p>
            <p>We'll automatically renew each of your domain names annually using your default payment method to keep your site running smoothly.</p>
            <p><strong>Can't see your newly registered domain?</strong> It can sometimes take a few minutes to show.</p>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view :companyName="company.name" />
  </div>
</template>

<script>
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'

import {mapState} from 'vuex'

import {readCompanyData} from '@/lib/companies'
import {readCompanyDomainsData} from '@/lib/domains'

import Table from '@/components/Table'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import Sprite from '@/components/Sprite'
import Btn from '@/components/Btn'

export default {
  name: 'company-domains',

  mixins: [page, pagination],

  props: {
    companyId: {
      type: String,
      required: true
    }
  },

  components: {
    TableData: Table,
    PageHeader,
    PageSummary,
    Sprite,
    Btn
  },

  data() {
    return {
      company: {},
      domains: [],
      domainSearchResults: []
    }
  },

  methods: {
    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      readCompanyDomainsData(this.companyId, this.pagination).then(
        ({data, page}) => {
          this.domains = data
          this.pageData = page
        }
      )
    }
  },

  computed: {
    ...mapState(['currentUser']),

    title() {
      return `${this.company.name} Domain Names`
    },

    hasDomains() {
      return !!this.domains.length
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-domains
  @extend %page

  .btn-refresh
    margin-right: $gap-small

  .domains-intro
    text-align: center
    margin: $gap-medium auto
    padding-bottom: $gap-medium
    max-width: 550px

    .sprite
      fill: rgba(0, 0, 0, 0.05)
      width: 160px
      height: 160px
      margin-bottom: $gap-medium
</style>
